import { z } from "zod";

export const DashboardIconSchema = z.enum(["stethoscope", "phone", "alert", "trending"]);
export const ChartPeriodSchema = z.enum(["30days", "3months", "currentYear", "previousYear"]);
export const ActivityTypeSchema = z.enum(["call", "alert", "report"]);
export const ActivitySeveritySchema = z.enum(["normal", "critical"]);
export const PatientFollowUpStatusSchema = z.enum(["critical", "warning", "success"]);
export const DashboardAlertVariantSchema = z.enum(["active", "neutral"]);

export const DashboardKPISchema = z.object({
  id: z.string(),
  label: z.string(),
  value: z.union([z.string(), z.number()]),
  change: z.string().optional(),
  changeType: z.enum(["positive", "negative", "neutral"]).optional(),
  description: z.string().optional(),
  icon: DashboardIconSchema,
  iconColor: z.string(),
  iconBgColor: z.string(),
});

export const ChartDataPointSchema = z.object({
  date: z.string(),
  withAlert: z.number(),
  withoutAlert: z.number(),
});

export const ImpactChartPointSchema = z.object({
  month: z.string(),
  hours: z.number(),
});

export const ImpactDataSchema = z.object({
  title: z.string(),
  value: z.string(),
  description: z.string(),
  chartData: z.array(ImpactChartPointSchema),
  equivalentText: z.string(),
});

export const PatientFollowUpSchema = z.object({
  followUpId: z.string(),
  name: z.string(),
  avatarUrl: z.string().optional(),
  avatarBgColor: z.string(),
  protocol: z.string(),
  status: PatientFollowUpStatusSchema,
  statusLabel: z.string().optional(),
  statusValue: z.string().optional(),
  lastCallTime: z.string(),
});

export const ActivityItemSchema = z.object({
  id: z.string(),
  type: ActivityTypeSchema,
  message: z.string(),
  time: z.string(),
  severity: ActivitySeveritySchema.optional(),
});

export const DashboardAlertBannerSchema = z.object({
  patientsCount: z.number(),
  variant: DashboardAlertVariantSchema,
});

export const WorkspaceDashboardGreetingSchema = z.object({
  name: z.string(),
  date: z.string(),
});

export const WorkspaceDashboardCoreSchema = z.object({
  greeting: WorkspaceDashboardGreetingSchema,
  alert: DashboardAlertBannerSchema.nullish(),
  kpis: z.array(DashboardKPISchema),
  followUps: z.array(PatientFollowUpSchema),
  activities: z.array(ActivityItemSchema),
});

export const WorkspaceDashboardCallSeriesPointSchema = z.object({
  bucketStart: z.string(),
  withAlert: z.number(),
  withoutAlert: z.number(),
});

export const WorkspaceDashboardCallSeriesSchema = z.object({
  period: ChartPeriodSchema,
  points: z.array(WorkspaceDashboardCallSeriesPointSchema),
});

export const WorkspaceDashboardCallMinutesPointSchema = z.object({
  bucketStart: z.string(),
  totalMinutes: z.number(),
});

export const WorkspaceDashboardCallMinutesSchema = z.object({
  totalMinutes: z.number(),
  totalHoursRounded: z.number(),
  equivalentDays: z.number(),
  points: z.array(WorkspaceDashboardCallMinutesPointSchema),
});

export type DashboardKPI = z.infer<typeof DashboardKPISchema>;
export type ChartPeriod = z.infer<typeof ChartPeriodSchema>;
export type ChartDataPoint = z.infer<typeof ChartDataPointSchema>;
export type ImpactData = z.infer<typeof ImpactDataSchema>;
export type PatientFollowUp = z.infer<typeof PatientFollowUpSchema>;
export type ActivityItem = z.infer<typeof ActivityItemSchema>;
export type DashboardAlertBanner = z.infer<typeof DashboardAlertBannerSchema>;
export type WorkspaceDashboardCore = z.infer<typeof WorkspaceDashboardCoreSchema>;
export type WorkspaceDashboardCallSeries = z.infer<typeof WorkspaceDashboardCallSeriesSchema>;
export type WorkspaceDashboardCallMinutes = z.infer<typeof WorkspaceDashboardCallMinutesSchema>;
